import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  GridValueGetterParams,
  GridColDef,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useParams } from "react-router-dom";
import Link from '@mui/material/Link';
import Typography from "@mui/material/Typography";
import Button from '@mui/material/Button/Button';
import Box from "@mui/material/Box/Box";
import AddIcon from '@mui/icons-material/Add';
import EditIcon1 from '@mui/icons-material/Edit';
// import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CrossIcon from '@mui/icons-material/Close';
import SecurityIcon from '@mui/icons-material/Security';

import {
  useAppDispatch,
  useAppSelector,
} from '../../store/hooks';

import CustomDataGrid from '../../components/DataGrid/DataGrid';
import BreadCrum from "../../components/Breadcrumbs/Breadcrumbs";
import { /* deleteUser,  */fetchUsers } from '../../store/userThunk';
import { /* IRole, */ IUserDetails } from "../../utils/user.types";
import { PermissionsEnum } from "../../utils/auth.types";
import { ApiStatus, apiLimit, defaultRowsPerPageOptions } from "../../utils/api.types";

import UserInviteDialog from "./UserInviteDialog";
import { Avatar, Card, CardContent, Grid, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Switch, FormControlLabel, TextField, Select, MenuItem } from "@mui/material";
import { Refresh, Check } from "@mui/icons-material";
import { fetchRoles } from '../../store/rolesThunk';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import InventoryEmptyState from '../../components/Inventory/InventoryEmptyState';
import '../../components/Invoice/Invoice.css';
import UserEditDrawer from "./UserEditDrawer";
import UserPermissionsView from "../../components/Permissions/UserPermissionsView";
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import permissionService from '../../services/PermissionService';
import { canManageUser } from '../../utils/roleHierarchy';

/**
 * Components to show users list in the data grid.
 */
const User = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { accountId = '' } = useParams();

  const { selectedAccount } = useAppSelector(state => state.auth);
  const { users, total, status } = useAppSelector(state => state.users);
  const { user, permissions, modulePermissions } = useAppSelector(state => state.auth);

  const canCreateUsers = permissions?.includes(PermissionsEnum.Users_Create);
  // const canUpdateUsers = permissions?.includes(PermissionsEnum.Users_Update);
  const canViewUsers = permissions?.includes(PermissionsEnum.Users_View);
  // const canDeleteUsers = permissions?.includes(PermissionsEnum.Users_Delete);

  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<any>(null);

  // Account-wide "enforce module permissions" flag. Owner-only toggle; when OFF
  // (default) grants are recorded but not enforced, so no user is locked out.
  const [enforcement, setEnforcement] = useState(false);
  const [enforcementSaving, setEnforcementSaving] = useState(false);

  // Server-side search / role filter / pagination.
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(apiLimit.limit);
  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([]);

  // Role dropdown options. Same source and mapping as the invite dialog.
  useEffect(() => {
    const loadRoles = async () => {
      const data: any = await dispatch(fetchRoles());
      const roles = Array.isArray(data?.payload) ? data.payload : [];
      setRoleOptions(roles.map((r: any) => ({
        label: r.role.charAt(0).toUpperCase() + r.role.slice(1),
        value: r._id
      })));
    };
    loadRoles();
  }, [dispatch]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0); // a new search always starts from the first page
  };

  const handleRoleChange = (value: string) => {
    setRoleFilter(value);
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearch('');
    setRoleFilter('');
    setPage(0);
  };

  // Distinguishes "nothing matched your filters" from "this account has no members".
  const hasFilters = !!search || !!roleFilter;

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  useEffect(() => {
    if (!canCreateUsers) return;
    let active = true;
    permissionService.getEnforcement()
      .then((val) => { if (active) setEnforcement(val); })
      .catch(() => { /* leave default off */ });
    return () => { active = false; };
  }, [canCreateUsers, selectedAccount]);

  const handleToggleEnforcement = async (checked: boolean) => {
    const prev = enforcement;
    setEnforcement(checked); // optimistic
    setEnforcementSaving(true);
    try {
      const saved = await permissionService.setEnforcement(checked);
      setEnforcement(saved);
      toast.success(`Permission enforcement ${saved ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      setEnforcement(prev); // revert
      toast.error(err?.response?.data?.message || 'Failed to update permission enforcement');
    } finally {
      setEnforcementSaving(false);
    }
  };

  /**
   * Fetch all the users list on user's page load.
   */
  useEffect(() => {
    // Fetch account related users if we are loading users on account details page.
    const id = accountId || (selectedAccount && selectedAccount._id);
    if (!id) return;

    const params = [
      search ? `search=${encodeURIComponent(search)}` : '',
      roleFilter ? `role=${roleFilter}` : '',
      `page=${page}`,
      `limit=${pageSize}`
    ].filter(Boolean).join('&');

    // Debounced so typing in the search box fires one request, not one per key.
    const timer = setTimeout(() => {
      dispatch(fetchUsers({ accountId: id, params }));
    }, 400);

    return () => clearTimeout(timer);
  }, [dispatch, selectedAccount, accountId, open, search, roleFilter, page, pageSize]);



  /**
   * Method to open user Form.
   */
  const handleAddUser = () => {
    setOpen(true);
  };

  /**
   * Handler for user name click.
   */
  const handleUserNameClick = useCallback((({ _id }: IUserDetails) => {
    if (accountId) {
      navigate(`/users/user-details/${_id}`, { state: { accountId } });
    } else {
      navigate(`/users/user-details/${_id}`);
    }
  }), [navigate, accountId]);

  /**
   * Method to handle delete user.
   */
  // const deleteSelectedUser = useCallback((user: IUserDetails) => () => {
  //   // Dispatch action to delete user.
  //   let userAccountId = accountId;
  //   if (!userAccountId) {
  //     userAccountId = selectedAccount._id;
  //   }
  //   dispatch(deleteUser({ userId: user._id, accountId: userAccountId }));
  // }, [dispatch, accountId, selectedAccount]);

  /**
   * Method to handle edit user.
   */
  const editSelectedUser = useCallback((userRow: any) => () => {
    let row = { ...userRow };
    row.accountId = selectedAccount._id;
    setSelectedUser(row);
    setDrawerOpen(true);
  }, [selectedAccount]);

  const drawerClose = () => { setDrawerOpen(false) };

  /**
   * Method to handle permissions dialog
   */
  const handleManagePermissions = useCallback((userRow: any) => () => {
    setSelectedUserForPermissions({
      _id: userRow._id,
      name: userRow.name,
      email: userRow.email
    });
    setPermissionsDialogOpen(true);
  }, []);

  const handleClosePermissions = () => {
    setPermissionsDialogOpen(false);
    setSelectedUserForPermissions(null);
  };

  const formatRoleText = (val: any) => {
    const roleValue = typeof val === 'string' ? val : val?.role;

    if (!roleValue) {
      return '';
    }
    switch (val?.role) {
      case 'admin':
        return 'Admin';
      case 'superAdmin':
        return 'Super-Admin'
      case 'memeber':
        return 'Member'
      default:
        return val?.role ? val.role.charAt(0).toUpperCase() + val.role.slice(1) : '';
    }

    if (roleValue === 'superAdmin') {
      return 'Super Admin';
    }

    return roleValue
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (char: string) => char.toUpperCase());
  }

  const checkIsUserVerifiedInAccount = (val: any) => {
    if (val?.length && val.length > 0) {
      return val[0].isUserVerified;
    }
    return false;
  };


  // Define Users data grid column defs.
  const columns: GridColDef[] = useMemo(() => [
    {
      field: "name",
      headerName: 'Name',
      type: "string",
      flex: 1.5,
      minWidth: 200,
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <Tooltip title={value || ''} arrow placement="top">
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', overflow: 'hidden', width: '100%' }}
            component={Link}
            underline='hover'
            onClick={() => handleUserNameClick(row)}
          >
            <Avatar
              alt={value?.toUpperCase()}
              imgProps={{ "referrerPolicy": 'no-referrer' }}
              src={`${row.profilePic}`}
              sx={{ width: 36, height: 36, flexShrink: 0 }}
            />
            <Typography
              sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {value}
            </Typography>
          </Box>
        </Tooltip>
      )
    },
    {
      field: "email",
      headerName: 'Email',
      type: "string",
      flex: 1,
      renderCell: ({ value }: GridValueGetterParams) => (
        <Tooltip title={value || ''} arrow placement="top">
          <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value}
          </Typography>
        </Tooltip>
      )
    },
    {
      field: "phone",
      headerName: 'Phone',
      type: "string",
      flex: 1
    },
    {
      field: "isEmailVerified",
      headerName: 'Email Verified',
      type: "string",
      renderCell: ({ row, value }: GridValueGetterParams) => (
        <div className="flex-column padIcon30">{value ? <CheckIcon /> : <CrossIcon />}</div>
      ),
      flex: 1
    },
    // {
    //   field: "isActive",
    //   headerName: 'Is Active',
    //   type: "string",
    //   renderCell: ({ row, value }: GridValueGetterParams) => (
    //     <div className="flex-column padIcon memberEditCell">{value ? <CheckIcon /> : <CrossIcon />}</div>
    //   ),
    //   flex: 1
    //   // flex: 1,
    //   // valueGetter: ({ value }: GridValueGetterParams) => {
    //   //   return checkIsUserVerifiedInAccount(value)

    //   //}
    // },

    // {
    //   field: 'accounts',
    //   headerName: 'Accounts',
    //   type: "singleSelect",
    //   flex: 1,
    //   valueGetter: ({ value }: GridValueGetterParams) => value[0]?.name ?? ''
    // },
    {
      field: 'role',
      headerName: 'Role',
      type: "singleSelect",
      flex: 1,
      valueGetter: ({ value }: GridValueGetterParams) => {
        return formatRoleText(value)
      }
    },
    {
      field: "status",
      headerName: 'Status',
      type: "string",
      flex: 0.8,
      renderCell: ({ row, value }: GridValueGetterParams) => {
        const role = row.useraccountroles.filter((r: any) => { return r.account === row.accounts[0]._id })
        return role && role[0] && role[0].isUserVerified ? 'Active' : 'Invited'
      },
    },
    {
      field: "accounts",
      headerName: 'Actions',
      type: "actions",
      flex: 0.8,
      getActions: (params: GridValueGetterParams) => {
        let isDisabled = params.row.accounts.filter((a: any) => a.owner === user._id && user._id === params.row._id).length > 0 ? true : false;

        // Seniority gate, mirroring the API: you cannot edit your own role or
        // permissions, and you can only manage someone below you (lower is more
        // senior on roles.roleId) — so an Admin is off limits to anyone but a
        // Super Admin or the account owner. The server rejects these anyway;
        // this keeps the icons honest.
        const targetIsOwner = params.row.accounts.some((a: any) => String(a.owner) === String(params.row._id));
        const canManage = canManageUser(
          {
            userId: user?._id,
            roleId: user?.role?.[0]?.roleId,
            hasFullAccess: modulePermissions?.hasFullAccess,
            isAccountOwner: modulePermissions?.isAccountOwner,
          },
          {
            userId: params.row._id,
            roleId: params.row?.role?.roleId,
            hasFullAccess: !!params.row?.hasFullAccess,
            isAccountOwner: targetIsOwner,
          }
        );
        isDisabled = isDisabled || !canManage;

        const editAction = (
          <GridActionsCellItem
            disabled={isDisabled}
            icon={<EditIcon1 />}
            title={canManage ? 'Edit' : 'You are not allowed to edit this member'}
            label="Edit"
            onClick={editSelectedUser(params.row)}
          />
        );
        const permissionsAction = (
          <GridActionsCellItem
            disabled={isDisabled}
            icon={<SecurityIcon />}
            title={canManage ? 'Manage Permissions' : 'You are not allowed to manage this member’s permissions'}
            label="Permissions"
            onClick={handleManagePermissions(params.row)}
          />
        );
        const actions: JSX.Element[] = [];
        actions.push(editAction);
        actions.push(permissionsAction);
        return actions;
      }

    }
    // {
    //   field: "actions",
    //   headerName: 'Actions',
    //   type: "actions",
    //   flex: 1,
    //   getActions: (params: GridValueGetterParams) => {
    //     const editAction = (
    //       <GridActionsCellItem
    //         icon={<EditIcon />}
    //         title="Edit"
    //         label="Edit"
    //         onClick={editSelectedUser(params.row)}
    //       />
    //     );

    //     const deleteAction = (
    //       <GridActionsCellItem
    //         icon={<DeleteIcon />}
    //         title="Delete"
    //         label="Delete"
    //         onClick={deleteSelectedUser(params.row)}
    //       />
    //     );

    //     const actions: JSX.Element[] = [];

    //     // Push edit action if user has update permission.
    //     if (canUpdateUsers && params.row._id !== user._id) {
    //       actions.push(editAction);
    //     }

    //     // Push edit action if user has delete permission.
    //     if (canDeleteUsers && params.row._id !== user._id) {
    //       actions.push(deleteAction);
    //     }

    //     return actions;
    //   }
    // }
  ], [
    // deleteSelectedUser,
    editSelectedUser,
    // canUpdateUsers,
    // canDeleteUsers,
    handleUserNameClick,
    handleManagePermissions,
    user,
    modulePermissions
  ]);

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);
  // const onSubmit = (data: any) => {
  //   console.log(data);
  //   // Dispatch login action with email and password payload.
  //   // const { name, email, phone, password } = data;

  //   // dispatch(register({ name, email, phone, password }))
  // };

  return (
    <>
      {/* <BreadCrum /> */}
      <Box sx={{ paddingTop: '16px' }}>

          {/* Toolbar — filters on the left (same look as the Sales list filters in
              Filters.tsx), enforcement toggle and Add button on the right. */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', gap: 2, paddingTop: '8px', px: '24px', width: '100%', flexWrap: 'wrap' }}>
            <div style={{ display: "flex", flexDirection: 'row', justifyContent: 'flex-start', alignItems: "center", textAlign: "start", minWidth: 0 }}>
              <IconButton onClick={handleResetFilters}
                style={{ marginRight: '15px', marginLeft: '5px', color: '#488dff', backgroundColor: '#fff', border: '3px solid #e9ecf1', float: 'left' }}
                size='small'><Refresh /></IconButton>

              <TextField
                className='c-text-field-3'
                variant="standard"
                name="search"
                size='small'
                style={{ width: 300, float: 'left' }}
                placeholder={`Type here to search member ...`}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{ disableUnderline: true }}
              />

              <Select
                name="role"
                disableUnderline
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1.5px solid #e9ecf1",
                      boxShadow: 'none',
                      borderRadius: '5px'
                    }
                  }
                }}
                sx={{
                  boxShadow: "none",
                  width: 200,
                  ml: '10px !important',
                  pt: '0px !important',
                  pl: '10px !important',
                  '& .MuiOutlinedInput-input': { p: 0, m: 0 },
                  '& .MuiSelect-select': { p: 0 },
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    border: 0,
                  },
                }}
                className='c-text-field-3'
                id="role"
                variant="outlined"
                value={roleFilter}
                renderValue={(selected) =>
                  selected
                    ? (roleOptions.find(o => o.value === selected)?.label || 'Select Role')
                    : 'Select Role'
                }
                onChange={(e) => handleRoleChange(e.target.value as string)}>
                <MenuItem value={''} key={'all'}>All Roles</MenuItem>
                {roleOptions.map(option => (
                  <MenuItem
                    value={option.value}
                    key={option.value}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {option.label}
                    {roleFilter === option.value &&
                      <Check fontSize="small" sx={{ color: '#16a34a', ml: 2 }} />}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <Box sx={{ display: "flex", justifyContent: "end", alignItems: 'center', gap: 2 }}>
              {canCreateUsers && (
                <Tooltip title="When ON, users are restricted to the modules/sub-modules granted in Manage Access. When OFF, permissions are recorded but not enforced.">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enforcement}
                        onChange={(e) => handleToggleEnforcement(e.target.checked)}
                        disabled={enforcementSaving}
                        color="primary"
                      />
                    }
                    label="Enforce Permissions"
                    sx={{ mr: 0, color: '#5a6472' }}
                  />
                </Tooltip>
              )}
              {canCreateUsers && <Button
                startIcon={<AddIcon />}
                variant="contained"
                color='secondary'
                onClick={handleAddUser}
              >
                Add New Member
              </Button>}
            </Box>
          </Box>

          <Box sx={{ width: "100%", marginTop: '24px' }}>
            {canViewUsers && (
              status !== ApiStatus.LOADING && (users?.length || 0) === 0 ? (
                hasFilters ? (
                  <InventoryEmptyState
                    icon={<PeopleAltOutlinedIcon />}
                    title="No Members Found"
                    description="No members match the current filters. Try adjusting your search or the selected role."
                    height="85vh"
                  />
                ) : (
                  <InventoryEmptyState
                    icon={<PeopleAltOutlinedIcon />}
                    title="No Members Yet"
                    description="Invite your team to this account. You can assign each member a role and fine-tune what they can access."
                    actionLabel={canCreateUsers ? "Add Your First Member" : undefined}
                    onAction={canCreateUsers ? handleAddUser : undefined}
                    height="85vh"
                  />
                )
              ) : (
                <Box sx={{ height: "85vh", width: "100%" }}>
                  <CustomDataGrid
                    columns={columns}
                    rows={users}
                    rowCount={total}
                    rowsPerPageOptions={defaultRowsPerPageOptions}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    getRowId={(row) => row._id}
                    loading={status === ApiStatus.LOADING}
                  />
                </Box>
              )
            )}
          </Box>
          <UserInviteDialog open={open} title="Member Invite" onClose={handleClose} />
          <UserEditDrawer userRow={selectedUser} open={drawerOpen} title="Change Member" onClose={drawerClose} />

          {/* Permissions Management Dialog */}
          <Dialog
            open={permissionsDialogOpen}
            onClose={handleClosePermissions}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              style: { minHeight: '70vh' }
            }}
          >
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                Manage User Permissions
                <IconButton onClick={handleClosePermissions}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {selectedUserForPermissions && (
                <UserPermissionsView
                  user={selectedUserForPermissions}
                  onClose={handleClosePermissions}
                />
              )}
            </DialogContent>
          </Dialog>
      </Box>
    </>
  )
}

export default User;
